import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { lmsToolConfiguration, getLmsToolConfiguration } from "../api/settingsApi";
import { toast } from "sonner";

interface LmsToolRegistrationProps {
  primaryColor: string;
}

function LmsToolRegistration({ primaryColor }: LmsToolRegistrationProps) {
  const [showForm, setShowForm] = useState(false);

  const lmsToolsQuery = useQuery({
    queryKey: ["/lms-tools"],
    queryFn: getLmsToolConfiguration,
    refetchOnWindowFocus: false,
  });

  const lmsConfigMutation = useMutation({
    mutationFn: lmsToolConfiguration,
    onSuccess: () => {
      toast.success("LMS configuration saved successfully");
      lmsToolsQuery.refetch();
      setShowForm(false);
    },
    onError: () => {
      toast.error("Failed to save LMS configuration");
    },
  });

  const platforms = lmsToolsQuery.data?.data?.platforms || [];

  if (showForm) {
    return (
    <Formik
      enableReinitialize
      initialValues={{
        platformName: "",
        platformId: "",
        clientId: "",
        authLoginUrl: "",
        authTokenUrl: "",
        keySetUrl: "",
        deploymentId: "",
      }}
      validationSchema={Yup.object({
        platformName: Yup.string().required("Platform Name is required"),
        platformId: Yup.string()
          .url("Invalid URL")
          .required("Platform ID is required"),
        clientId: Yup.string().required("Client ID is required"),
        authLoginUrl: Yup.string()
          .url("Invalid URL")
          .required("Auth Login URL is required"),
        authTokenUrl: Yup.string()
          .url("Invalid URL")
          .required("Auth Token URL is required"),
        keySetUrl: Yup.string()
          .url("Invalid URL")
          .required("Keyset URL is required"),
        deploymentId: Yup.string().required("Deployment ID is required"),
      })}
      onSubmit={(values: any, { setSubmitting }: any) => {
        setSubmitting(true);
        lmsConfigMutation.mutate(values);
        setSubmitting(false);
      }}
    >
      {({ isSubmitting, handleReset }) => (
        <Form>
          <div className="w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Add LMS Tool</h2>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ← Back to List
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Platform Name
                </label>
                <Field
                  name="platformName"
                  type="text"
                  className="w-full border rounded px-3 py-2"
                />
                <ErrorMessage
                  name="platformName"
                  component="div"
                  className="text-xs text-red-500 mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Platform ID
                </label>
                <Field
                  name="platformId"
                  type="text"
                  className="w-full border rounded px-3 py-2"
                />
                <ErrorMessage
                  name="platformId"
                  component="div"
                  className="text-xs text-red-500 mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Client ID
                </label>
                <Field
                  name="clientId"
                  type="text"
                  className="w-full border rounded px-3 py-2"
                />
                <ErrorMessage
                  name="clientId"
                  component="div"
                  className="text-xs text-red-500 mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Auth Login URL
                </label>
                <Field
                  name="authLoginUrl"
                  type="text"
                  className="w-full border rounded px-3 py-2"
                />
                <ErrorMessage
                  name="authLoginUrl"
                  component="div"
                  className="text-xs text-red-500 mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Auth Token URL
                </label>
                <Field
                  name="authTokenUrl"
                  type="text"
                  className="w-full border rounded px-3 py-2"
                />
                <ErrorMessage
                  name="authTokenUrl"
                  component="div"
                  className="text-xs text-red-500 mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Keyset URL
                </label>
                <Field
                  name="keySetUrl"
                  type="text"
                  className="w-full border rounded px-3 py-2"
                />
                <ErrorMessage
                  name="keySetUrl"
                  component="div"
                  className="text-xs text-red-500 mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Deployment ID
                </label>
                <Field
                  name="deploymentId"
                  type="text"
                  className="w-full border rounded px-3 py-2"
                />
                <ErrorMessage
                  name="deploymentId"
                  component="div"
                  className="text-xs text-red-500 mt-1"
                />
              </div>
            </div>
          </div>
          <div className="flex gap-4 mt-8">
            <button
              type="submit"
              style={{ backgroundColor: primaryColor }}
              className="text-white px-6 py-2 rounded font-semibold"
              disabled={isSubmitting}
            >
              Save
            </button>
            <button
              type="button"
              style={{ color: primaryColor, borderColor: primaryColor }}
              className="px-6 py-2 rounded font-semibold border"
              onClick={handleReset}
              disabled={isSubmitting}
            >
              Reset
            </button>
          </div>
        </Form>
      )}
    </Formik>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold">LMS Tool Registration</h2>
        <button
          onClick={() => setShowForm(true)}
          style={{ backgroundColor: primaryColor }}
          className="text-white px-4 py-2 rounded font-semibold hover:opacity-90 transition-opacity"
        >
          + Add Tool
        </button>
      </div>

      {lmsToolsQuery.isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : platforms.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No LMS tools configured yet. Click "Add Tool" to get started.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {platforms.map((tool: any) => (
            <div
              key={tool.id}
              className="border rounded-lg p-6 bg-white shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold text-lg"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {tool.platformName.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">
                      {tool.platformName}
                    </h3>
                    <span className="text-xs text-gray-500">
                      ID: {tool.clientId}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm">
                  <span className="text-gray-500 w-24">Platform:</span>
                  <span className="text-gray-700 truncate" title={tool.platformId}>
                    {tool.platformId}
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  <span className="text-gray-500 w-24">Deployments:</span>
                  <span className="text-gray-700">{tool.deploymentCount}</span>
                </div>
                <div className="flex items-center text-sm">
                  <span className="text-gray-500 w-24">Status:</span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    tool.isActive
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-700"
                  }`}>
                    {tool.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    Created:{" "}
                    <span className="font-semibold text-gray-700">
                      {new Date(tool.createdAt).toLocaleDateString()}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default LmsToolRegistration;
